<template>
  <div class="header">
    <div class="header-left">
      <button @click="goHome" class="btn-back" title="Retour à l'accueil">
        ← Accueil
      </button>
      <div v-if="currentHypothesis" class="hypothesis-info">
        <span class="hypothesis-name">{{ currentHypothesis.name }}</span>
        <span v-if="saveStatus" :class="['save-status', `save-status-${saveStatus}`]">
          <span class="material-symbols-outlined save-status-icon">{{ saveStatusIcon }}</span>
          {{ saveStatusText }}
        </span>
        <button
          @click="handleEditHypothesis(currentHypothesis)"
          class="btn-edit-hypothesis"
          title="Modifier l'hypothèse"
        >
          <span class="material-symbols-outlined">edit</span>
        </button>
        <button
          @click="handleDuplicateHypothesis"
          class="btn-edit-hypothesis"
          title="Dupliquer l'hypothèse"
        >
          <span class="material-symbols-outlined">content_copy</span>
        </button>
      </div>
    </div>
    <div class="header-center">
      <HypothesisSelector
        @open-create-modal="showCreateModal = true"
        @open-edit-modal="handleEditHypothesis"
      />
    </div>
    <div class="header-right">
      <button
        v-if="currentHypothesis"
        :disabled="!canUndo"
        class="btn-icon-header"
        title="Annuler"
        @click="handleUndo"
      >
        <span class="material-symbols-outlined">undo</span>
      </button>
      <button
        v-if="currentHypothesis"
        :disabled="!canRedo"
        class="btn-icon-header"
        title="Restaurer"
        @click="handleRedo"
      >
        <span class="material-symbols-outlined">redo</span>
      </button>
      <ExportButton />
      <button @click="handleImport" class="btn-import">+ Importer bloc</button>
    </div>

    <HypothesisModal
      :show="showCreateModal || showEditModal"
      :hypothesis="editingHypothesis"
      @close="closeModal"
      @created="handleHypothesisCreated"
      @updated="handleHypothesisUpdated"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useHypothesesStore } from '@stores/hypotheses.store';
import HypothesisSelector from './HypothesisSelector.vue';
import ExportButton from './ExportButton.vue';
import HypothesisModal from './HypothesisModal.vue';

const router = useRouter();
const hypothesesStore = useHypothesesStore();

const currentHypothesis = computed(() => hypothesesStore.currentHypothesis);
const canUndo = computed(() => hypothesesStore.canUndo);
const canRedo = computed(() => hypothesesStore.canRedo);
const saveStatus = ref<'saved' | 'saving' | 'error' | null>(null);
let saveStatusTimeout: ReturnType<typeof setTimeout> | null = null;

const saveStatusIcon = computed(() => {
  switch (saveStatus.value) {
    case 'saved':
      return 'check_circle';
    case 'saving':
      return 'progress_activity';
    case 'error':
      return 'error';
    default:
      return '';
  }
});

const saveStatusText = computed(() => {
  switch (saveStatus.value) {
    case 'saved':
      return 'Sauvegardé';
    case 'saving':
      return 'Sauvegarde...';
    case 'error':
      return 'Erreur';
    default:
      return '';
  }
});

const handleSaveStatus = (event: CustomEvent) => {
  saveStatus.value = event.detail.status;
  
  // Réinitialiser le statut après 2 secondes si sauvegardé
  if (saveStatusTimeout) {
    clearTimeout(saveStatusTimeout);
  }
  
  if (event.detail.status === 'saved') {
    saveStatusTimeout = setTimeout(() => {
      saveStatus.value = null;
    }, 2000);
  } else if (event.detail.status === 'error') {
    saveStatusTimeout = setTimeout(() => {
      saveStatus.value = null;
    }, 5000);
  }
};

// Surveiller les changements dans l'hypothèse pour mettre à jour le statut
watch(
  () => currentHypothesis.value,
  () => {
    if (currentHypothesis.value) {
      saveStatus.value = 'saved';
      if (saveStatusTimeout) {
        clearTimeout(saveStatusTimeout);
      }
      saveStatusTimeout = setTimeout(() => {
        saveStatus.value = null;
      }, 2000);
    }
  }
);

onMounted(() => {
  window.addEventListener('save-status', handleSaveStatus as EventListener);
});

onUnmounted(() => {
  window.removeEventListener('save-status', handleSaveStatus as EventListener);
  if (saveStatusTimeout) {
    clearTimeout(saveStatusTimeout);
  }
});

const goHome = () => {
  router.push('/');
};

const showCreateModal = ref(false);
const showEditModal = ref(false);
const editingHypothesis = ref<any>(null);

const handleImport = () => {
  // Déclencher l'ouverture du modal d'import depuis BlocksLibrary
  const event = new CustomEvent('open-import-modal');
  window.dispatchEvent(event);
};

const handleUndo = async () => {
  try {
    await hypothesesStore.undo();
    window.dispatchEvent(new CustomEvent('save-status', { detail: { status: 'saved' } }));
  } catch {
    window.dispatchEvent(new CustomEvent('save-status', { detail: { status: 'error' } }));
  }
};

const handleRedo = async () => {
  try {
    await hypothesesStore.redo();
    window.dispatchEvent(new CustomEvent('save-status', { detail: { status: 'saved' } }));
  } catch {
    window.dispatchEvent(new CustomEvent('save-status', { detail: { status: 'error' } }));
  }
};

const handleEditHypothesis = (hypothesis: any) => {
  editingHypothesis.value = hypothesis;
  showEditModal.value = true;
};

const closeModal = () => {
  showCreateModal.value = false;
  showEditModal.value = false;
  editingHypothesis.value = null;
};

const handleHypothesisCreated = (hypothesis: any) => {
  router.push(`/workspace/${hypothesis.id}`);
};

const handleHypothesisUpdated = () => {
  // L'hypothèse est automatiquement mise à jour dans le store
  closeModal();
};

const handleDuplicateHypothesis = async () => {
  if (!currentHypothesis.value) return;
  try {
    const created = await hypothesesStore.duplicate(currentHypothesis.value.id);
    router.push(`/workspace/${created.id}`);
  } catch (err: any) {
    window.dispatchEvent(
      new CustomEvent('save-status', { detail: { status: 'error' } })
    );
  }
};
</script>

<style scoped>
.header {
  height: 60px;
  background-color: #2a2a2a;
  border-bottom: 1px solid #444;
  display: flex;
  align-items: center;
  padding: 0 1rem;
  gap: 1rem;
}

.header-left,
.header-center,
.header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-left {
  flex: 1;
}

.header-center {
  flex: 1;
  justify-content: center;
}

.header-right {
  flex: 1;
  justify-content: flex-end;
}

.btn-back {
  padding: 0.5rem 1rem;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.btn-back:hover {
  background-color: #444;
  border-color: #555;
}

.hypothesis-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-edit-hypothesis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #999;
  cursor: pointer;
  transition: all 0.2s;
  width: 32px;
  height: 32px;
}

.btn-edit-hypothesis:hover {
  background-color: #444;
  color: #e0e0e0;
  border-color: #4a9eff;
}

.btn-edit-hypothesis .material-symbols-outlined {
  font-size: 16px;
}

.btn-icon-header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #999;
  cursor: pointer;
  transition: all 0.2s;
  width: 32px;
  height: 32px;
}

.btn-icon-header:hover:not(:disabled) {
  background-color: #444;
  color: #e0e0e0;
  border-color: #555;
}

.btn-icon-header:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-icon-header .material-symbols-outlined {
  font-size: 20px;
}

.hypothesis-name {
  font-weight: 600;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.save-status {
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.save-status-icon {
  font-size: 1rem;
}

.save-status-saved {
  color: #28a745;
}

.save-status-saving {
  color: #ffc107;
}

.save-status-error {
  color: #dc3545;
}

.btn-import {
  padding: 0.5rem 1rem;
  background-color: #4a9eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-import:hover {
  background-color: #3a8eef;
}
</style>
